import { unsupportedUploadFormat, unsupportedUploadMessage } from './upload-formats.js';
import { checkInputBytes } from './input-limits.js';
import { checkDatasetLimit } from './dataset-limits.js';

export async function readInputFiles(entries, readBinary, { directory = false, onProgress, otherBytes = 0, single = false } = {}) {
  if (directory) checkDatasetLimit('discoveredEntries', entries.length);
  const parts = entries.filter(({ path }) => /\.parquet$/i.test(path));
  const excludedPaths = entries.filter(({ path }) => !/\.parquet$/i.test(path)).map(({ path }) => path);
  if (!directory && entries.length === 1 && !parts.length) return readInputFile(entries[0].file, readBinary, { otherBytes, single });
  if (!directory && excludedPaths.length) throw new Error('Multiple-file selection accepts only .parquet parts. No files were loaded.');
  if (!parts.length) throw new Error(`No Parquet files found${excludedPaths.length ? ` (${excludedPaths.length} non-Parquet files found)` : ''}. Select a folder containing .parquet files.`);
  checkInputBytes(parts.reduce((sum, entry) => sum + (entry.file.size || 0), 0), otherBytes, { single });
  checkDatasetLimit('files', parts.length);
  checkDatasetLimit('bytes', parts.reduce((sum, entry) => sum + (entry.file.size || 0), 0));
  // Browser File objects can be cloned into the worker without materializing
  // every buffer on the main thread. Test/adaptor files may expose only arrayBuffer.
  const files = [];
  for (const { path, file } of parts) {
    files.push(typeof file.slice === 'function' && typeof file.size === 'number'
      ? { path, file } : { path, buffer: await file.arrayBuffer() });
  }
  return readBinary({ format: 'parquet', files, dataset: directory || parts.length > 1, excludedPaths }, { onProgress });
}

/** File input foundation: binary formats never pass through File.text(). */
export async function readInputFile(file, readBinary, { otherBytes = 0, single = false } = {}) {
  const unsupported = unsupportedUploadFormat(file);
  if (unsupported) throw new Error(unsupportedUploadMessage(unsupported));
  checkInputBytes(file.size || 0, otherBytes, { single });
  if (/\.parquet$/i.test(file.name)) {
    return readBinary({ format: 'parquet', buffer: await file.arrayBuffer() });
  }
  return { kind: 'text', text: await file.text() };
}

export async function readBinaryInput({ format, buffer, files, dataset, excludedPaths }, onProgress) {
  if (format !== 'parquet') throw new Error('Unsupported binary file format.');
  if (files) {
    const { readParquetDataset } = await import('./parquet-dataset.js');
    return readParquetDataset(files, { dataset, excludedPaths, onProgress });
  }
  const { readParquet } = await import('./parquet.js');
  return readParquet(buffer);
}
